import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppBar } from "../components/AppBar";
import { Photo } from "../components/Photo";
import { useAuth } from "../lib/auth";
import { listMyRecipes, listRevisions, setRecipeArchived } from "../lib/queries";
import { fmtDateShort } from "../lib/format";
import type { Recipe, Revision } from "../lib/types";

type View = "grid" | "list";
const VIEW_KEY = "affinage.cookbook.view";

// Editorial section ordering — anything not in this list falls to the bottom alphabetically.
const CATEGORY_ORDER = [
  "breakfast",
  "brunch",
  "lunch",
  "dinner",
  "snacks",
  "sides",
  "dessert",
  "desserts",
  "drinks",
  "smoothies",
  "smoothie",
  "uncategorized",
];

type Group = { key: string; label: string; recipes: Recipe[] };

function groupByCategory(items: Recipe[]): Group[] {
  const buckets = new Map<string, Recipe[]>();
  for (const r of items) {
    const k = (r.category ?? "uncategorized").toLowerCase();
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k)!.push(r);
  }
  const keys = [...buckets.keys()].sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a);
    const bi = CATEGORY_ORDER.indexOf(b);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.localeCompare(b);
  });
  return keys.map((k) => ({
    key: k,
    label: k === "uncategorized" ? "Other" : k.charAt(0).toUpperCase() + k.slice(1),
    recipes: buckets.get(k)!,
  }));
}

export function Cookbook() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [staple, setStaple] = useState<{ recipe: Recipe; revs: Revision[] } | null>(null);
  const [cat, setCat] = useState<string | null>(null);
  // The archive is a separate shelf, not a category — browsing it replaces the
  // grid entirely rather than filtering it.
  const [showArchive, setShowArchive] = useState(false);
  const [view, setView] = useState<View>(() => {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem(VIEW_KEY) : null;
    return stored === "list" ? "list" : "grid";
  });

  useEffect(() => {
    window.localStorage.setItem(VIEW_KEY, view);
  }, [view]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const all = await listMyRecipes(user.uid);
        if (cancelled) return;
        setRecipes(all);
        // The shelves are ready now — show them. The staple's revision history
        // is a detail of one card, and making the whole cookbook wait on a
        // second round trip for it meant staring at a spinner with the recipes
        // already in hand.
        setLoading(false);

        const featured = all.find((r) => !r.archived) ?? null;
        if (featured) {
          // Render the hero immediately with no revisions; the count fills in
          // when it arrives. A failure here leaves the card standing rather
          // than taking the page down with it.
          setStaple({ recipe: featured, revs: [] });
          const revs = await listRevisions(user.uid, featured.id).catch(() => []);
          if (!cancelled) setStaple({ recipe: featured, revs });
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message ?? "Could not load your cookbook.");
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // The shelves hold everything that hasn't been archived; the archive holds
  // the rest, newest put away first.
  const active = useMemo(() => recipes.filter((r) => !r.archived), [recipes]);
  const archived = useMemo(
    () =>
      recipes
        .filter((r) => r.archived)
        .sort((a, b) => (b.archivedAt ?? "").localeCompare(a.archivedAt ?? "")),
    [recipes]
  );

  async function restore(id: string) {
    if (!user) return;
    setRecipes((prev) =>
      prev.map((r) => (r.id === id ? { ...r, archived: false, archivedAt: undefined } : r))
    );
    try {
      await setRecipeArchived(user.uid, id, false);
    } catch (e: any) {
      setError(e?.message ?? "Could not restore that recipe.");
    }
  }

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: active.length };
    for (const r of active) {
      const k = (r.category ?? "uncategorized").toLowerCase();
      c[k] = (c[k] ?? 0) + 1;
    }
    return c;
  }, [active]);

  const visible = useMemo(
    () => (cat ? active.filter((r) => (r.category ?? "").toLowerCase() === cat) : active),
    [active, cat]
  );

  // Skip staple from its own section so we don't duplicate it under the grid hero
  const groupedAll = useMemo(() => groupByCategory(visible), [visible]);
  const groupedForGrid = useMemo(
    () =>
      groupByCategory(
        staple && cat == null ? visible.filter((r) => r.id !== staple.recipe.id) : visible
      ),
    [visible, staple, cat]
  );

  return (
    <>
      <AppBar active="kitchen" />
      <div className="page cookbook fade-in">
        <div
          className="cookbook-head"
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 16,
            marginBottom: 20,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div className="eb">YOUR COOKBOOK</div>
            <div className="h1" style={{ marginTop: 6, fontSize: "clamp(36px, 5vw, 52px)" }}>
              {user?.displayName ? user.displayName.split(" ")[0] + "'s cookbook" : "Your cookbook"}
            </div>
          </div>
          <div style={{ display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap" }}>
            <div
              data-tour-id="category-chips"
              style={{ display: "flex", gap: 18, alignItems: "baseline", flexWrap: "wrap" }}
            >
              <CatChip
                label="ALL"
                count={counts.all ?? 0}
                active={cat == null && !showArchive}
                onClick={() => {
                  setCat(null);
                  setShowArchive(false);
                }}
              />
              {Object.entries(counts)
                .filter(([k]) => k !== "all")
                .sort((a, b) => b[1] - a[1])
                .slice(0, 6)
                .map(([k, n]) => (
                  <CatChip
                    key={k}
                    label={k.toUpperCase()}
                    count={n}
                    active={cat === k && !showArchive}
                    onClick={() => {
                      setCat(k);
                      setShowArchive(false);
                    }}
                  />
                ))}
              {archived.length > 0 && (
                <CatChip
                  label="ARCHIVED"
                  count={archived.length}
                  active={showArchive}
                  onClick={() => {
                    setShowArchive(true);
                    setCat(null);
                  }}
                />
              )}
            </div>
            <ViewToggle view={view} onChange={setView} />
            <Link to="/recipe/new" className="btn primary" data-tour-id="new-recipe">
              + NEW RECIPE
            </Link>
          </div>
        </div>

        {error && <div className="banner error">{error}</div>}

        {loading ? (
          <div className="empty">
            <span className="spinner" /> &nbsp; Loading your cookbook…
          </div>
        ) : showArchive ? (
          <ArchiveShelf
            recipes={archived}
            onOpen={(id) => navigate(`/recipe/${id}`)}
            onRestore={restore}
          />
        ) : active.length === 0 ? (
          <EmptyCookbook archivedCount={archived.length} onSeeArchive={() => setShowArchive(true)} />
        ) : view === "list" ? (
          <div style={{ marginTop: 24 }}>
            {groupedAll.map((g, gi) => (
              <section key={g.key} className="cat-section">
                <CategoryHeader label={g.label} count={g.recipes.length} />
                <div className="recipe-list">
                  {g.recipes.map((r, ri) => (
                    <CookbookListRow
                      key={r.id}
                      recipe={r}
                      onOpen={() => navigate(`/recipe/${r.id}`)}
                      tourId={gi === 0 && ri === 0 ? "recipe-card-first" : undefined}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <>
            {staple && cat == null && (
              <FeaturedStaple
                recipe={staple.recipe}
                revs={staple.revs}
                onOpen={() => navigate(`/recipe/${staple.recipe.id}`)}
                onRevs={() => navigate(`/recipe/${staple.recipe.id}/revisions`)}
                tourId="recipe-card-first"
              />
            )}

            {groupedForGrid.map((g, gi) => (
              <section key={g.key} className="cat-section">
                <CategoryHeader label={g.label} count={g.recipes.length} />
                <div
                  className="grid-3"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 32,
                  }}
                >
                  {g.recipes.map((r, ri) => (
                    <CookbookCard
                      key={r.id}
                      recipe={r}
                      onOpen={() => navigate(`/recipe/${r.id}`)}
                      tourId={!staple && gi === 0 && ri === 0 ? "recipe-card-first" : undefined}
                    />
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </div>
      <style>{`
        @media (max-width: 900px) {
          .grid-3 { grid-template-columns: 1fr 1fr !important; gap: 20px !important; }
        }
        @media (max-width: 600px) {
          .grid-3 { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
}

function CategoryHeader({ label, count }: { label: string; count: number }) {
  return (
    <div className="cat-header">
      <div className="cat-header-row">
        <h2 className="h2 cat-title">{label}</h2>
        <span className="eb cat-count">
          {count} RECIPE{count === 1 ? "" : "S"}
        </span>
      </div>
    </div>
  );
}

function ViewToggle({ view, onChange }: { view: View; onChange: (v: View) => void }) {
  return (
    <div className="view-toggle" role="group" aria-label="View" data-tour-id="view-toggle">
      <button
        className={view === "grid" ? "active" : undefined}
        onClick={() => onChange("grid")}
        aria-pressed={view === "grid"}
      >
        <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <rect x="1" y="1" width="6" height="6" />
          <rect x="9" y="1" width="6" height="6" />
          <rect x="1" y="9" width="6" height="6" />
          <rect x="9" y="9" width="6" height="6" />
        </svg>
        Grid
      </button>
      <button
        className={view === "list" ? "active" : undefined}
        onClick={() => onChange("list")}
        aria-pressed={view === "list"}
      >
        <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <rect x="1" y="2" width="14" height="2" />
          <rect x="1" y="7" width="14" height="2" />
          <rect x="1" y="12" width="14" height="2" />
        </svg>
        List
      </button>
    </div>
  );
}

function CookbookListRow({ recipe, onOpen, tourId }: { recipe: Recipe; onOpen: () => void; tourId?: string }) {
  const meta = [
    recipe.servings ? `${recipe.servings} SERV${recipe.servings === 1 ? "" : "ES"}` : null,
    recipe.macros?.protein != null ? `${recipe.macros.protein}G PROTEIN` : null,
    recipe.macros?.calories != null ? `${recipe.macros.calories} KCAL` : null,
  ].filter(Boolean);
  return (
    <div className="recipe-list-row" onClick={onOpen} data-tour-id={tourId}>
      <div
        className={"thumb" + (recipe.imageUrl ? "" : " empty")}
        style={recipe.imageUrl ? { backgroundImage: `url("${recipe.imageUrl}")` } : undefined}
        aria-hidden="true"
      />
      <div>
        <div className="title">{recipe.title}</div>
        {recipe.subtitle && <div className="subtitle">{recipe.subtitle}</div>}
      </div>
      <div className="meta">
        {meta.map((m, i) => (
          <span key={i} className="eb">{m}</span>
        ))}
      </div>
      <span className="eb open">OPEN ›</span>
    </div>
  );
}

function CatChip({ label, count, active, onClick }: { label: string; count: number; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="eb"
      style={{
        background: "none",
        border: "none",
        color: active ? "var(--accent)" : undefined,
        padding: 0,
        letterSpacing: 3,
      }}
    >
      {label} · {count}
    </button>
  );
}

function FeaturedStaple({
  recipe,
  revs,
  onOpen,
  onRevs,
  tourId,
}: {
  recipe: Recipe;
  revs: Revision[];
  onOpen: () => void;
  onRevs: () => void;
  tourId?: string;
}) {
  const cookedLabel = recipe.category ? recipe.category.toUpperCase() : "STAPLE";
  const revsAsc = [...revs].sort((a, b) => a.number - b.number);
  const current = revs.find((r) => r.number === recipe.currentRevisionNumber) ?? revs[0];
  return (
    <div
      className="featured"
      onClick={onOpen}
      data-tour-id={tourId}
      style={{
        cursor: "pointer",
        display: "grid",
        gridTemplateColumns: "1.45fr 1fr",
        gap: 0,
        marginTop: 24,
        alignItems: "stretch",
        border: "1px solid var(--line)",
      }}
    >
      <div style={{ position: "relative", minHeight: 360 }}>
        <Photo src={recipe.imageUrl} placeholder="Drop a hero photo for this recipe" fill caption={false} eager />
      </div>
      <div style={{ padding: "32px 36px" }}>
        <div className="eb" style={{ marginBottom: 14 }}>
          THE STAPLE · {cookedLabel}
        </div>
        <div className="h1" style={{ fontSize: 40 }}>
          {recipe.title}
        </div>
        {recipe.subtitle && (
          <div className="subtitle-italic" style={{ fontSize: 20, marginTop: 6 }}>
            {recipe.subtitle}
          </div>
        )}
        {recipe.description && (
          <div
            className="dropcap"
            style={{
              marginTop: 20,
              fontFamily: "var(--font-body-serif)",
              fontSize: 15,
              color: "var(--fg)",
              lineHeight: 1.75,
              maxWidth: 380,
            }}
          >
            {recipe.description}
          </div>
        )}

        {revs.length > 0 && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              onRevs();
            }}
            style={{
              marginTop: 22,
              paddingTop: 18,
              borderTop: "1px solid var(--line)",
              cursor: "pointer",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <span className="eb">
                {revs.length} REFINEMENT{revs.length === 1 ? "" : "S"}
                {current?.savedAt
                  ? ` · CURRENT IS ${new Date(current.savedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }).toUpperCase()}`
                  : ""}
              </span>
              <span className="eb" style={{ color: "var(--accent)" }}>
                SEE ALL ›
              </span>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {revsAsc.map((r) => {
                const isCurrent = r.number === recipe.currentRevisionNumber;
                const isMilestone = !!r.releaseName;
                return (
                  <div
                    key={r._docId}
                    title={`#${r.number}${r.note ? " — " + r.note : ""}`}
                    style={{
                      flex: 1,
                      height: 6,
                      background: isCurrent ? "var(--accent)" : isMilestone ? "var(--green)" : "var(--dim)",
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 12, marginTop: 22, alignItems: "center", flexWrap: "wrap" }}>
          <Link
            to={`/recipe/${recipe.id}`}
            onClick={(e) => e.stopPropagation()}
            className="btn primary"
          >
            OPEN PAGE
          </Link>
        </div>
      </div>
    </div>
  );
}

function CookbookCard({ recipe, onOpen, tourId }: { recipe: Recipe; onOpen: () => void; tourId?: string }) {
  const eb = [
    recipe.servings ? `${recipe.servings} SERVING${recipe.servings === 1 ? "" : "S"}` : null,
    recipe.macros?.protein != null ? `${recipe.macros.protein}G PROTEIN` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div
      onClick={onOpen}
      data-tour-id={tourId}
      style={{ cursor: "pointer", display: "flex", flexDirection: "column" }}
    >
      <Photo src={recipe.imageUrl} placeholder="Drop a recipe photo" ratio="4 / 3" />
      {eb && (
        <div className="eb" style={{ marginTop: 14 }}>
          {eb}
        </div>
      )}
      <div className="h2" style={{ marginTop: eb ? 6 : 14, fontSize: 26 }}>
        {recipe.title}
      </div>
      {recipe.subtitle && (
        <div className="subtitle-italic" style={{ fontSize: 15, marginTop: 2 }}>
          {recipe.subtitle}
        </div>
      )}
      {recipe.description && (
        <div
          style={{
            marginTop: 10,
            color: "var(--fg-muted)",
            fontFamily: "var(--font-serif)",
            fontSize: 13,
            lineHeight: 1.7,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical" as const,
            overflow: "hidden",
          }}
        >
          {recipe.description}
        </div>
      )}
    </div>
  );
}

// The archive: everything put away, newest first, one tap from coming back.
function ArchiveShelf({
  recipes,
  onOpen,
  onRestore,
}: {
  recipes: Recipe[];
  onOpen: (id: string) => void;
  onRestore: (id: string) => void;
}) {
  if (recipes.length === 0) {
    return (
      <div className="empty" style={{ paddingTop: 96 }}>
        <div className="h2" style={{ marginBottom: 12 }}>
          Nothing archived.
        </div>
        <div style={{ maxWidth: 460, margin: "0 auto", lineHeight: 1.7 }}>
          Archiving takes a recipe off the shelves without losing it — every revision
          stays, and you can put it back any time.
        </div>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 24 }}>
      <div className="cat-header">
        <div className="cat-header-row">
          <h2 className="h2 cat-title">Archived</h2>
          <span className="eb cat-count">
            {recipes.length} RECIPE{recipes.length === 1 ? "" : "S"}
          </span>
        </div>
      </div>
      <div className="subtitle-italic" style={{ fontSize: 15, marginBottom: 14 }}>
        Off the shelves, fully intact — revisions, links, and anything already planned
        still work.
      </div>
      <div className="recipe-list">
        {recipes.map((r) => (
          <div className="recipe-list-row archived-row" key={r.id} onClick={() => onOpen(r.id)}>
            <div
              className={"thumb" + (r.imageUrl ? "" : " empty")}
              style={r.imageUrl ? { backgroundImage: `url("${r.imageUrl}")` } : undefined}
              aria-hidden="true"
            />
            <div>
              <div className="title">{r.title}</div>
              {r.subtitle && <div className="subtitle">{r.subtitle}</div>}
            </div>
            <div className="meta">
              {r.archivedAt && <span className="eb">ARCHIVED {fmtDateShort(r.archivedAt).toUpperCase()}</span>}
            </div>
            <button
              className="btn"
              onClick={(e) => {
                e.stopPropagation();
                onRestore(r.id);
              }}
            >
              RESTORE
            </button>
          </div>
        ))}
      </div>
      <style>{`
        .archived-row .thumb { filter: grayscale(1); opacity: 0.75; }
        .archived-row .btn { padding: 7px 12px; }
      `}</style>
    </div>
  );
}

function EmptyCookbook({
  archivedCount = 0,
  onSeeArchive,
}: {
  archivedCount?: number;
  onSeeArchive?: () => void;
}) {
  return (
    <div className="empty" style={{ paddingTop: 96 }}>
      <div className="h2" style={{ marginBottom: 12 }}>
        Nothing to tend yet.
      </div>
      {archivedCount > 0 && (
        <div style={{ marginBottom: 14 }}>
          Everything you have is archived.{" "}
          <button
            onClick={onSeeArchive}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              color: "var(--accent)",
              cursor: "pointer",
              font: "inherit",
            }}
          >
            See the {archivedCount} archived recipe{archivedCount === 1 ? "" : "s"} ›
          </button>
        </div>
      )}
      <div style={{ maxWidth: 460, margin: "0 auto", lineHeight: 1.7 }}>
        Write a recipe yourself, ask your assistant — Claude, ChatGPT, Cursor — to add one,
        or wander over to{" "}
        <Link to="/community" style={{ color: "var(--accent)" }}>
          The Kitchen Table
        </Link>{" "}
        and copy one you'd actually cook.
      </div>
      <div style={{ marginTop: 24 }}>
        <Link to="/recipe/new" className="btn primary">
          + NEW RECIPE
        </Link>
      </div>
    </div>
  );
}
